import { Component, OnInit, HostListener, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { NgxUiLoaderService } from 'ngx-ui-loader';

import { UserService } from '../shared/services/user.service';
import { UserAuthService } from '../shared/services/auth/user-auth.service';
import { CommonHttpClient } from '../shared/services/common/common-http.service';

import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';

// external helpers that your HTML uses (sorting/search)
// declare function searchFunction(): any;
// declare function sortTableExpDate(): any;
// declare function sortTableDateAdded(): any;
// declare function sortTableString(columnIndex: number): any;

export type SubuserRole = 'Member' | 'Owner' | 'Manager';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit {

  // ====== existing dashboard fields (kept) ======
  user: any;
  rateId: string;
  amount: number;
  isActive: boolean;
  subscriptionDetails: any;
  apiCalled = false;
  isFreeUser = false;
  showNoData = false;
  transId: string;
  error: string = null;
  showExpiredCardMessage = false;
  showTerminatedSubMessage = false;
  fam_id: any;
  private dependentsInitFired = false;
  // current table sort state
  currentSortColumn: 'date_added' | 'date_expire' | 'first_name' | 'last_name' | 'email_id' = 'date_added';
  currentSortDirection: 'asc' | 'desc' = 'desc';

  // ====== dependant users table: migrated fields ======
  readonly roles: SubuserRole[] = ['Member', 'Owner', 'Manager'];

  // intl-phone input
  separateDialCode = true;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.India];

  // plan / ownership context
  planName: string;
  logged_user_role: SubuserRole;
  main_user_id: string;          // current user id
  main_plan_user_id: string;     // owner id if current user is Manager
  owner_display_name: string;    // display name of the plan owner

  // seats & table data
  totalSeatsGET = 0;
  formData: any[] = [];          // all dependants
  filteredData: any[] = [];      // filtered by Active/Inactive
  paginatedData: any[] = [];     // page slice
  seatsTakenCount = 0;
  active_user_counter = 0;
  inActiveUsers = 0;
  isSeatsFull = false;

  // modal + view flags
  startSection = true;
  deleteUserPop = false;

  // form helpers used by the template
  signSubmit = false;
  errorExpDays: string | null = null;

  // pagination sizes used by the <select>
  pageSizes: number[] = [500000, 5, 10, 20, 50, 100];

  // pagination
  currentPage = 1;
  itemsPerPage = 500000; // show all by default
  totalPages = 0;

  // UI state
  addUser = true;                // keep table visible for non-free plans
  addUserPop = false;            // modal
  editUser = false;
  isSubmitBtnDisabled = false;
  dropdownVisible = false;
  selectedActiveStatus: 'all' | 'active' | 'inactive' =
    (sessionStorage.getItem('currentFilter') as any) || 'all';

  // form & edit helpers
  userForm: FormGroup;
  editRecordId: string | null = null;
  userToDelete: any = null;
  resendDropdownVisible: { [key: string]: boolean } = {};

  // transient values for create/edit
  phone_number = '';
  email = '';
  first_name = '';
  last_name = '';
  display_name = '';
  country_code = '';

  // libphonenumber
  PNF: any;
  phoneUtil: any;
  plan_id: any;
  pricePerSeat: any;

  // user who hasn't activated yet
  confirmationToken: string = null;
  currentUserEmail: string = null;
  currentUserPhone: string = null;

  constructor(
    private userService: UserService,
    private userAuth: UserAuthService,
    private _location: Location,
    private commonHttpInit: CommonHttpClient,
    private ngxloader: NgxUiLoaderService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.user = this.userAuth.getStoredUser();
    this.initUserForm();

    // libphonenumber (kept like in PlansComponent)
    this.PNF = require('google-libphonenumber').PhoneNumberFormat;
    this.phoneUtil = require('google-libphonenumber').PhoneNumberUtil.getInstance();
  }

  ngOnInit(): void {
    this.initCommonHTTP();

    // Safety net: Ensure loader stops after maximum 5 seconds
    setTimeout(() => {
      if (this.ngxloader.getLoader()) {
        console.warn('Loader timeout safety net triggered');
        this.ngxloader.stop();
      }
    }, 5000);
  }

  // ========== bootstrap data ==========
  async initCommonHTTP() {
    this.ngxloader.start();
    this.getSubscriptionDetails();
    var key = await this.commonHttpInit.getApiKey()
    if (key) {
      this.getSubscriptionId();
    }
  }

  getSubscriptionDetails() {
    const onLoaded = (resp: any) => {
      if (resp?.message === 'success') {
        this.subscriptionDetails = resp;
        this.rateId = resp.rate_id;
        this.isActive = resp.is_active;  // This is crucial - set BEFORE loadDependents
        this.transId = resp.sub_id;

        // Capture confirmation token and user contact info
        this.confirmationToken = resp.confirmation_key;
        this.currentUserEmail = resp.email_id;
        this.currentUserPhone = resp.phone_number;

        // capture role + ownership context
        this.logged_user_role = resp.role as SubuserRole;
        this.main_user_id = resp.id;
        this.main_plan_user_id = resp.main_user_id;
        this.owner_display_name = resp.display_name;

        // >>> ensure dependants load immediately
        this.loadDependents();

        if (this.rateId) {
          this.userService.getRateAmount(this.rateId).subscribe(rateResp => {
            this.amount = rateResp.Amount;
            this.isFreeUser = !(this.amount > 0);
            this.apiCalled = true;

            // For free users or users without management roles, ensure loader stops
            if (this.isFreeUser || (!['Owner', 'Manager'].includes(this.logged_user_role))) {
              setTimeout(() => {
                this.stopLoaderWhenTableReady();
              }, 500);
            } else {
              // optional: calling again is safe (guarded)
              this.loadDependents();
            }
          }, _ => {
            this.apiCalled = true;
            this.waitForTableToRender();
          });
        } else {
          this.rateId = '643faa83249ee74859b0c8ca';
          this.apiCalled = true;
          this.waitForTableToRender();
        }
      } else {
        this.showNoData = true;
        this.apiCalled = true;
        this.ngxloader.stop();
      }
    };

    if (this.user?.includes('@')) {
      this.userService.getSubscriptionDetailsWithEmail(this.user).subscribe(onLoaded, _ => {
        this.showNoData = true;
        this.apiCalled = true;
        this.ngxloader.stop();
      });
    } else {
      this.userService.getSubscriptionDetailsWithPhoneNumber(this.user).subscribe(onLoaded, _ => {
        this.showNoData = true;
        this.apiCalled = true;
        this.ngxloader.stop();
      });
    }
  }

  getSubscriptionId() {
    const key = localStorage.getItem('0');
    if (!key) {
      this.ngxloader.stop(); // Stop loader if no key
      return;
    }

    const call$ = key.includes('@')
      ? this.userService.getSubscriptionIdWithEmail(key)
      : this.userService.getSubscriptionIdWithPhone(key);

    call$.subscribe(resp => {
      if (resp.message !== 'success') {
        this.planName = 'Dependant';
        this.stopLoaderWhenTableReady();
        return;
      }

      // common assignments
      this.logged_user_role = resp.role;
      this.transId = resp.sub_id;
      this.totalSeatsGET = resp.seats;
      this.main_plan_user_id = resp.main_user_id;
      this.main_user_id = resp.id;

      // For users with 0 seats, ensure loader stops
      if (this.totalSeatsGET === 0) {
        this.planName = 'Dependant';
        setTimeout(() => {
          this.ngxloader.stop();
        }, 500);
        return;
      }

      if (this.rateId) {
        this.userService.getRateAmount(this.rateId).subscribe(rateResp => {
          this.plan_id = rateResp.PlanId;
          this.amount = rateResp.Amount;
          this.pricePerSeat = rateResp.SeatPrice;
          this.userService.getPlanName(this.plan_id).subscribe(planResp => {
            this.planName = planResp.Name;
            if (planResp.Name !== "Free") {
              this.addUser = true;
              this.loadDependents();
              setTimeout(() => {
                //sortTableDateAdded(), 1000
              });
            } else if (this.logged_user_role === 'Manager' && this.main_plan_user_id) {
              this.addUser = true;
              this.loadDependents();
              this.planName = "Dependant";
              setTimeout(() => {
                //sortTableDateAdded(), 1000
              });
            }
            else {
              this.planName = "Dependant";
              this.stopLoaderWhenTableReady();
            }
          }, error => { this.stopLoaderWhenTableReady(); })
        }, error => { this.stopLoaderWhenTableReady(); })
      }
      else {
        this.planName = "Dependant";
        this.stopLoaderWhenTableReady();
      }
    },
      () => {
        this.error = 'Error!';
        this.ngxloader.stop(); // Direct stop on error
      });
  }

  // Fixed loadDependents method
  loadDependents() {
    if (this.dependentsInitFired) return;

    // require role to be known
    if (!this.logged_user_role) return;

    // mark fired now to avoid double fetches
    this.dependentsInitFired = true;

    if (this.logged_user_role === 'Owner') {
      this.getDependentUsers(this.user);
    } else if (this.logged_user_role === 'Manager' && this.main_plan_user_id) {
      this.getDependentUsers(this.main_plan_user_id);
    } else {
      // For Dependants/Members or users without seats
      // Stop the loader since there's nothing to load
      this.dependentsInitFired = false; // allow future attempt if role changes

      // Ensure loader stops for non-management users
      setTimeout(() => {
        this.stopLoaderWhenTableReady();
      }, 500);
    }
  }

  // ========== dependant users: data ==========
  getDependentUsers(keyToUse: string) {
    this.active_user_counter = 0;
    this.inActiveUsers = 0;

    const call$ = keyToUse.includes('@')
      ? this.userService.getDependentUsersEmail(keyToUse)
      : this.userService.getDependentUsersPhone(keyToUse);

    call$.subscribe((resp: { mainUser: any; dependents: any[] }) => {
      this.handleDependentUsers(resp);
    }, _ => {
      this.error = 'Error loading dependents!';
      this.ngxloader.stop();
    });
  }

  private handleDependentUsers(resp: {
    mainUser: { displayName: string; totalSeats: number },
    dependents: any[]
  }) {
    try {
      // Owner name & total seats
      this.owner_display_name = resp.mainUser?.displayName || '';
      this.totalSeatsGET = resp.mainUser?.totalSeats || 0;

      const users = resp.dependents || [];

      // Raw list used for filtering
      this.filteredData = [...users];

      this.sortFilteredData();

      // Apply current filter (all / active / inactive) on sorted data
      if (this.selectedActiveStatus === 'all') {
        this.formData = [...this.filteredData];
      } else {
        const wantActive = this.selectedActiveStatus === 'active';
        this.formData = this.filteredData.filter(u => !!u.is_active === wantActive);
      }

      // Count active / inactive
      this.active_user_counter = this.filteredData.filter(u => u.is_active).length;
      this.inActiveUsers = this.filteredData.length - this.active_user_counter;

      // Format phone numbers for display (kept, but per-user guarded)
      this.formData.forEach(u => {
        try {
          const intl = u?.phone_number?.internationalNumber;
          if (!intl) return;

          const region = typeof u.country_code === 'string'
            ? u.country_code.toUpperCase()
            : '';

          if (region && region.length === 2) {
            const parsed = this.phoneUtil.parseAndKeepRawInput(intl, region);
            if (this.phoneUtil.isValidNumber(parsed)) {
              u.phone_number.internationalNumber =
                this.phoneUtil.format(parsed, this.PNF.INTERNATIONAL);
            }
          } else {
            const parsedFallback = this.phoneUtil.parseAndKeepRawInput(intl);
            if (this.phoneUtil.isValidNumber(parsedFallback)) {
              u.phone_number.internationalNumber =
                this.phoneUtil.format(parsedFallback, this.PNF.INTERNATIONAL);
            }
          }
        } catch (e) {
          console.warn('Skipping invalid phone number for user', u, e);
        }
      });

      // Seats state - use filteredData (all users) not formData (filtered view)
      // Fixed: Only mark as full when totalSeatsGET > 0 AND all seats are taken
      this.seatsTakenCount = this.filteredData.length;
      this.isSeatsFull = this.totalSeatsGET > 0 && this.filteredData.length >= this.totalSeatsGET;
      this.isSubmitBtnDisabled = this.isSeatsFull;

      // Paging
      this.totalPages = Math.ceil(this.formData.length / this.itemsPerPage);
      this.updatePaginatedData();

      // Trigger change detection and stop loader after table render
      this.cdr.detectChanges();
      this.waitForTableToRender();
    } catch (error) {
      console.error('Error handling dependent users:', error);
      this.ngxloader.stop(); // Ensure loader stops on any error
    }
  }

  // ========== forms ==========
  get f() { return this.userForm.controls; }

  private initUserForm() {
    this.userForm = new FormGroup({
      id: new FormControl('', Validators.maxLength(40)),
      first_name: new FormControl('', [Validators.required, Validators.maxLength(40)]),
      last_name: new FormControl('', [Validators.required, Validators.maxLength(40)]),
      display_name: new FormControl('', [Validators.maxLength(40)]),
      email_id: new FormControl('', [
        Validators.required,
        Validators.pattern(/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/),
        Validators.maxLength(254)
      ]),
      phone_number: new FormControl('', [Validators.maxLength(50), Validators.minLength(3)]),
      expiry_date_days: new FormControl(''),
      date_added: new FormControl(''),
      date_expire: new FormControl(''),
      is_active: new FormControl(''),
      role: new FormControl('Member', Validators.required),
    }, { validators: [this.expDaysValidator] });
  }

  // ========== add / edit / delete ==========
  openModal() {
    this.addUserPop = true;
    this.userForm.reset({ role: 'Member' });
    // Managers cannot change role
    if (this.logged_user_role === 'Manager') this.userForm.get('role')?.disable();
    else this.userForm.get('role')?.enable();
    this.userForm.get('email_id')?.enable();
    this.userForm.get('phone_number')?.enable();
    this.editUser = false;
  }

  closeModal() {
    // Fixed: Only mark as full when totalSeatsGET > 0 AND all seats are taken
    if (this.totalSeatsGET > 0 && this.filteredData.length >= this.totalSeatsGET) {
      this.isSubmitBtnDisabled = true;
      this.isSeatsFull = true;
    }
    this.editUser = false;
    this.editRecordId = null;
    this.addUserPop = false;
  }

  onEdit(user: any) {
    this.editUser = true;
    user.id = user.date_added;
    user.display_name = `${user.first_name} ${user.last_name}`;

    // Calculate days to expire from the date_expire field
    let daysToExpire = null;
    if (user.date_expire) {
      const expiryDate = new Date(user.date_expire);
      const today = new Date();
      const diffTime = expiryDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // Only set if the expiry is in the future and within valid range
      if (diffDays > 0 && diffDays <= 90) {
        daysToExpire = diffDays;
      }
    }

    this.isSubmitBtnDisabled = false;
    this.editRecordId = user.id;

    this.userForm.setValue({
      id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      display_name: user.display_name,
      email_id: user.email_id,
      phone_number: user.phone_number.internationalNumber,
      expiry_date_days: daysToExpire,
      date_added: user.date_added,
      date_expire: user.date_expire,
      is_active: user.is_active,
      role: (user.role as SubuserRole) || 'Member'
    });

    this.userForm.get('email_id')?.disable();
    this.userForm.get('phone_number')?.disable();

    this.addUserPop = true;
    this.cdr.detectChanges();
  }

  confirmUserEdit() {
    this.userForm.get('email_id')?.enable();
    this.userForm.get('phone_number')?.enable();

    // Fixed: Only mark as full when totalSeatsGET > 0 AND all seats are taken
    if (this.totalSeatsGET > 0 && this.filteredData.length >= this.totalSeatsGET) {
      this.isSubmitBtnDisabled = true;
      this.isSeatsFull = true;
    }

    if (this.userForm.valid) {
      this.ngxloader.start();

      const firstName = this.userForm.get('first_name').value;
      const lastName = this.userForm.get('last_name').value;

      // expiry date calculation
      let dateToExpire: Date = null;
      const days = this.userForm.get('expiry_date_days').value;
      if (days > 0) {
        dateToExpire = new Date();
        dateToExpire.setDate(dateToExpire.getDate() + days);
      }

      const email = this.userForm.get('email_id').value;
      const updateMongoData = {
        first_name: firstName,
        last_name: lastName,
        date_expire: dateToExpire,
        display_name: `${firstName} ${lastName}`,
        role: this.userForm.get('role').value as SubuserRole
      };

      const onUpdated = () => {
        const updateFamData = { display_name: `${firstName} ${lastName}` };
        const famCall = email
          ? this.userService.updateTempUserMariaAPIByEmail(email, updateFamData)
          : this.userService.updateTempUserMariaAPIByPhone(this.phone_number, updateFamData);

        famCall.subscribe(famResp => {
          if (famResp.message === 'Temp user updated successfully!') {
            this.editUser = false;
            this.addUserPop = false;

            // Add a small delay before refreshing to ensure DB update is complete
            setTimeout(() => {
              if (this.logged_user_role === 'Owner') {
                this.getDependentUsers(this.user);
              } else if (this.logged_user_role === 'Manager' && this.main_plan_user_id) {
                this.getDependentUsers(this.main_plan_user_id);
              }
              this.ngxloader.stop();
            }, 500); // 500ms delay
          }
        });
      };

      if (email) {
        this.userService.updateSubscriptionWithEmail(email, updateMongoData).subscribe(resp => {
          if (resp.message === 'success') onUpdated();
        }, _ => this.ngxloader.stop());
      } else {
        const phoneNumber = this.userForm.get('phone_number').value.e164Number;
        this.phone_number = this.removePlusFromstring(phoneNumber);
        this.userService.updateSubscriptionWithPhoneNumber(this.phone_number, updateMongoData)
          .subscribe(resp => {
            if (resp.message === 'success') onUpdated();
          }, _ => this.ngxloader.stop());
      }
    } else {
      this.error = 'Please fill all required fields with valid details';
    }
  }

  confirmDelete(user: any) { this.userToDelete = user; this.deleteUserPop = true; }

  cancelDelete() { this.userToDelete = null; this.deleteUserPop = false; }

  onDelete() {
    if (!this.userToDelete) return;
    this.ngxloader.start();

    const user = this.userToDelete;
    const after = () => {
      this.userToDelete = null;
      this.deleteUserPop = false;
      document.body.classList.remove('modal-open-inline');

      // Call getDependentUsers directly, not loadDependents
      if (this.logged_user_role === 'Owner') {
        this.getDependentUsers(this.user);
      } else if (this.logged_user_role === 'Manager' && this.main_plan_user_id) {
        this.getDependentUsers(this.main_plan_user_id);
      }

      this.ngxloader.stop();
    };

    if (user.email_id) {
      this.userService.deleteAccountEmailFAM(user.email_id).subscribe(_ => {
        this.userService.deleteAccountEmail(user.email_id).subscribe(_ => after(), _ => after());
      }, _ => after());
    } else if (user.phone_number) {
      const phone = user.phone_number;
      this.userService.deleteAccountPhoneFAM(phone).subscribe(_ => {
        this.userService.deleteAccountPhone(phone).subscribe(_ => after(), _ => after());
      }, _ => after());
    } else {
      after();
    }
  }

  // ========== create user ==========
  verifyUserSubscription() {
    this.error = null;
    const phoneCtrl = this.userForm.get('phone_number')?.value;
    this.email = this.userForm.get('email_id')?.value;
    this.first_name = this.userForm.get('first_name')?.value;
    this.last_name = this.userForm.get('last_name')?.value;

    if (phoneCtrl?.e164Number) {
      this.phone_number = this.removePlusFromstring(phoneCtrl.e164Number);

      this.userService.validateUserPhoneNumber({ phone_number: this.phone_number }).subscribe(_ => {
        // phone exists -> try email
        if (this.email) {
          this.userService.validateUserEmailAddress({ email_id: this.email }).subscribe(_ => {
            this.error = 'This email is already registered';
          }, _ => this.createUser());
        }
      }, _ => {
        // phone does not exist -> check email or create
        if (this.email) {
          this.userService.validateUserEmailAddress({ email_id: this.email }).subscribe(_ => {
            this.error = 'This email is already registered';
          }, _ => this.createUser());
        } else {
          this.createUser();
        }
      });
    }
  }

  private generateRandomPassword(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const chars2 = '@#$!%^&';
    let password = '';
    for (let i = 0; i < 9; i++) password += chars.charAt(Math.floor(Math.random() * chars.length));
    const specialChar = chars2.charAt(Math.floor(Math.random() * chars2.length));
    const pos = Math.floor(Math.random() * (password.length + 1));
    return password.slice(0, pos) + specialChar + password.slice(pos);
  }

  createUser() {
    this.ngxloader.start();

    this.display_name = `${this.userForm.get('first_name').value} ${this.userForm.get('last_name').value}`;
    this.country_code = this.userForm.get('phone_number')?.value?.countryCode ?? '';
    const password = this.generateRandomPassword();

    const flexReq = this.userService.createFlexisipUser({
      username: `+${this.phone_number}`,
      password,
      algorithm: 'SHA-256',
      phone: `+${this.phone_number}`,
      email: this.email,
      display_name: this.display_name,
      activated: false
    });

    flexReq.subscribe((resp: any) => {
      const FAMID = resp.id;
      const token = resp.provisioning_token;

      // set expiry
      const now = new Date();
      const days = this.userForm.get('expiry_date_days').value;
      const date_expire = days > 0 ? new Date(now.getTime() + days * 86400000) : null;

      const basePayload: any = {
        ...this.userForm.value,
        phone_number: this.phone_number,
        is_active: false,
        password,
        display_name: this.display_name,
        fam_id: FAMID,
        rate_id: '643faa83249ee74859b0c8ca',
        date_added: now,
        date_expire,
        email_id: this.email,
        first_name: this.first_name,
        last_name: this.last_name,
        country_code: this.country_code,
        confirmation_key: token,
        role: this.userForm.get('role').value as SubuserRole
      };

      const payload = (this.logged_user_role === 'Manager')
        ? { ...basePayload, main_user_id: this.main_plan_user_id, manager_id: this.main_user_id }
        : { ...basePayload, main_user_id: this.main_user_id };

      this.userService.dependentUserCreate(payload).subscribe((createResp: any) => {
        if (createResp.message === 'User was registered successfully!') {
          const emailData = {
            to: this.email,
            phone_number: this.phone_number,
            password,
            from: this.owner_display_name || this.subscriptionDetails?.display_name,
            token
          };
          this.userService.sendUserInformation(emailData).subscribe(() => {
            this.ngxloader.stop();
            this.addUserRecord();    // refresh UI table
            this.addUserPop = false;
          });
        } else {
          this.error = createResp.message;
          this.ngxloader.stop();
        }
      }, _ => { this.error = 'Error creating user'; this.ngxloader.stop(); });
    }, _ => { this.error = 'Error provisioning user'; this.ngxloader.stop(); });
  }

  // update UI list after add/edit
  addUserRecord() {
    // reload from server to keep counts consistent
    if (this.editRecordId) {
      this.formData = this.formData.map((data) => data.id === this.editRecordId ? this.userForm.value : data)
      this.editRecordId = null;
    } else {
      // Fixed: Call getDependentUsers directly instead of loadDependents (which has a guard)
      if (this.logged_user_role === 'Manager' && this.main_plan_user_id) {
        this.getDependentUsers(this.main_plan_user_id);
      } else if (this.logged_user_role === 'Owner') {
        this.getDependentUsers(this.user);
      }

      if (this.totalSeatsGET > 0 && this.filteredData.length >= this.totalSeatsGET) {
        this.isSubmitBtnDisabled = true;
        this.isSeatsFull = true;
      }
    }
    this.updatePaginatedData();
    this.cdr.detectChanges();

    this.userForm.reset({ role: 'Member' });
  }

  // ========== resend / reset ==========
  resendUserInformation(user: any) {
    this.ngxloader.start();
    this.userService.getSubscriptionDetailsWithEmail(user.email_id).subscribe(resp => {
      const token = resp.confirmation_key;

      if (!user.is_active) {
        this.userService.sendUserInformation({
          to: user.email_id,
          phone_number: this.phone_number,
          from: this.owner_display_name,
          token
        }).subscribe(() => this.ngxloader.stop());
      } else {
        this.userService.sendUserPasswordReset({
          to: user.email_id,
          from: this.owner_display_name
        }).subscribe(() => this.ngxloader.stop());
      }
    });
  }

  sendDirectEmail(user: any) {
    this.ngxloader.start();
    this.closeAllResendDropdowns();
    this.userService.getSubscriptionDetailsWithEmail(user.email_id).subscribe(resp => {
      const token = resp.confirmation_key;
      this.userService.sendUserInformation({
        to: user.email_id,
        phone_number: user.phone_number,
        from: this.owner_display_name,
        token
      }).subscribe(() => this.ngxloader.stop());
    });
  }

  sendPersonalEmail(user: any) {
    this.closeAllResendDropdowns();
    this.userService.getSubscriptionDetailsWithEmail(user.email_id).subscribe(resp => {
      const token = resp.confirmation_key;
      const subject = encodeURIComponent('Titanium account confirmation');
      const verifyUrl = `https://www.titaniumsecure.io/validation-otp/${token}`;

      const body = encodeURIComponent(
        `Welcome to Titanium!\n\n` +
        `${this.owner_display_name} has set up an account for you. To activate your account, please verify your phone number using the link below and set your desired password.\n\n` +
        `Verify phone number: ${verifyUrl}\n\n` +
        `If you encounter any issues or have questions, please refer to our instructions page for guidance: https://www.titaniumsecure.io/instructions\n\n` +
        `This is a reminder email, you have previously received an email from auth. Please whitelist the domain titaniumcomms.com for future correspondents.\n\n` +
        `Best regards,\n${this.owner_display_name}`
      );

      window.location.href = `mailto:${user.email_id}?subject=${subject}&body=${body}`;
    });
  }

  //resend confirmation for the current logged-in user
  resendCurrentUserConfirmation() {
    if (!this.confirmationToken) {
      this.error = 'Unable to resend confirmation email. Missing confirmation token.';
      return;
    }

    this.ngxloader.start();

    // Use phone number to get subscription details
    const phoneToUse = this.user?.includes('@') ? this.currentUserPhone : this.user;

    this.userService.getSubscriptionDetailsWithPhoneNumber(phoneToUse).subscribe(resp => {
      const token = resp.confirmation_key;
      const email = resp.email_id;
      const phone = resp.phone_number;
      var linkData = { token: token, email: email }
      this.userService.sendConfirmationLink(linkData).subscribe(async (resp) => {
        if (resp.message === 'Email was successfully sent!') {
          this.ngxloader.stop();
          this.error = null;
          alert('Confirmation email has been resent successfully!');
        }
      }, err => {
        this.ngxloader.stop();
        this.error = 'Failed to resend confirmation email. Please try again.';
      });
    }, err => {
      this.ngxloader.stop();
      this.error = 'Failed to retrieve user details. Please try again.';
    });
  }

  // ========== dropdowns / filters ==========
  toggleResendDropdown(userId: string, event?: Event) {
    if (event) event.stopPropagation();
    Object.keys(this.resendDropdownVisible).forEach(k => {
      if (k !== userId) this.resendDropdownVisible[k] = false;
    });
    this.resendDropdownVisible[userId] = !this.resendDropdownVisible[userId];
  }

  closeAllResendDropdowns() {
    Object.keys(this.resendDropdownVisible).forEach(k => this.resendDropdownVisible[k] = false);
  }

  toggleDropdown() {
    this.dropdownVisible = !this.dropdownVisible;
    event?.stopPropagation();
  }

  filterTable() {
    this.ngxloader.start();
    this.dropdownVisible = false;
    this.applyFilterAndPaginate();
  }

  selectOption(status: 'all' | 'active' | 'inactive') {
    this.selectedActiveStatus = status;
    sessionStorage.setItem('currentFilter', this.selectedActiveStatus);
    this.filterTable();
  }

  // ========== pagination ==========
  updatePaginatedData(): void {
    const formData = this.formData;
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedData = this.formData.slice(startIndex, endIndex);
  }

  changePageSize(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.itemsPerPage = +selectElement.value;
    this.currentPage = 1;
    this.updateTotalPages();
    this.updatePaginatedData();
    this.cdr.detectChanges();
  }

  updateTotalPages(): void {
    this.totalPages = Math.max(1, Math.ceil(this.formData.length / this.itemsPerPage));
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePaginatedData();
      this.cdr.detectChanges();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePaginatedData();
      this.cdr.detectChanges();
    }
  }

  // ========== utilities ==========
  removePlusFromstring(phoneNumber: string) {
    return phoneNumber?.split('+').join('');
  }

  expDaysValidator: ValidatorFn = (control: AbstractControl) => {
    const v = control.get('expiry_date_days')?.value;
    if (v === null || v === '' || v === undefined) { this.errorExpDays = null; return null; }
    if (v < 1 || v > 90) { this.errorExpDays = 'Please enter the days from 1 to 90 or leave blank for permanent.'; return { expDaysRange: true }; }
    this.errorExpDays = null;
    return null;
  };

  logOut() {
    this.ngxloader.start();
    this.userService.logout();
    this.ngxloader.stop();
  }

  backClicked() {
    if (this.router.navigated) this._location.back();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.resend-dropdown-container')) this.closeAllResendDropdowns();
    if (!target.closest('.dropdown-btn') && !target.closest('.dropdown-content')) this.dropdownVisible = false;
  }

  // convenience
  canManageUsers(): boolean {
    return ['Owner', 'Manager'].includes(this.logged_user_role);
  }

  canChangePlan(): boolean {
    return this.logged_user_role === 'Owner';
  }

  private applyFilterAndPaginate(): void {
    if (!this.filteredData) {
      this.stopLoaderWhenTableReady();
      return;
    }

    // Apply active/inactive filter on already-sorted filteredData
    if (this.selectedActiveStatus === 'all') {
      this.formData = [...this.filteredData];
    } else {
      const wantActive = this.selectedActiveStatus === 'active';
      this.formData = this.filteredData.filter(u => !!u.is_active === wantActive);
    }

    // Recalculate counters
    this.active_user_counter = this.filteredData.filter(u => u.is_active).length;
    this.inActiveUsers = this.filteredData.length - this.active_user_counter;

    // Seats info - use filteredData (all users) for total count
    // Fixed: Only mark as full when totalSeatsGET > 0 AND all seats are taken
    this.seatsTakenCount = this.filteredData.length;
    this.isSeatsFull = this.totalSeatsGET > 0 && this.filteredData.length >= this.totalSeatsGET;
    this.isSubmitBtnDisabled = this.isSeatsFull;

    // Paging
    this.updateTotalPages();
    this.currentPage = Math.min(this.currentPage, this.totalPages);
    this.updatePaginatedData();

    // Removed: DOM sortTableDateAdded() call – sorting is now done in TS
    this.stopLoaderWhenTableReady();
    this.cdr.detectChanges();
  }

  private waitForTableToRender(): void {
    if (this.shouldShowTable()) {
      // Force change detection first
      this.cdr.detectChanges();

      // Use requestAnimationFrame to wait for browser paint
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Check if table actually exists in DOM
          const tableElement = document.getElementById('myTable');
          if (tableElement && tableElement.offsetParent !== null) {
            // Table is rendered and visible
            setTimeout(() => {
              this.ngxloader.stop();
            }, 300); // Small buffer for any animations
          } else {
            // Table not yet rendered, wait a bit more
            setTimeout(() => {
              this.ngxloader.stop();
            }, 1000);
          }
        });
      });
    } else {
      // Table won't show, stop immediately
      this.ngxloader.stop();
    }
  }

  private shouldShowTable(): boolean {
    return this.totalSeatsGET > 0 && this.startSection && this.isActive;
  }

  private stopLoaderWhenTableReady(): void {
    // Always stop the loader after a maximum timeout
    const maxTimeout = setTimeout(() => {
      this.ngxloader.stop();
    }, 2000); // Maximum 2 seconds

    if (this.shouldShowTable()) {
      // Table will be shown, wait for DOM rendering
      this.cdr.detectChanges();
      setTimeout(() => {
        clearTimeout(maxTimeout);
        this.ngxloader.stop();
      }, 800);
    } else {
      // Table won't show, stop immediately
      clearTimeout(maxTimeout);
      this.ngxloader.stop();
    }
  }
  /**
 * Sort filteredData by a column, then re-apply filter + pagination.
 */
  sortBy(column: 'date_added' | 'date_expire' | 'first_name' | 'last_name' | 'email_id'): void {
    if (this.currentSortColumn === column) {
      // toggle direction if clicking same column
      this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortColumn = column;
      this.currentSortDirection = 'asc';
    }

    this.sortFilteredData();
    this.applyFilterAndPaginate();
  }

  /**
   * Internal helper: sorts filteredData in-place according to currentSortColumn/Direction.
   */
  private sortFilteredData(): void {
    const dir = this.currentSortDirection === 'asc' ? 1 : -1;

    this.filteredData.sort((a, b) => {
      const col = this.currentSortColumn;

      if (col === 'date_added' || col === 'date_expire') {
        const aTime = a[col] ? new Date(a[col]).getTime() : 0;
        const bTime = b[col] ? new Date(b[col]).getTime() : 0;
        return (aTime - bTime) * dir;
      }

      // string columns
      const aVal = (a[col] ?? '').toString().toLowerCase();
      const bVal = (b[col] ?? '').toString().toLowerCase();
      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });
  }

}
